/**
 * ClaimReportBanner + ClaimReportModal — Phase-B smart EFGH-user detection.
 *
 * When a viewer opens a shared Smart Report link, we hit /public/reports/{token}/claim-hint.
 * If the report's stored contact matches an existing EFGH patient account, we surface a
 * banner offering to add the report to their Health Timeline via WhatsApp/email OTP.
 *
 * Idempotent — if already linked, banner switches to a success acknowledgement.
 * Public — no login required.
 */
import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { CheckCircle2, ArrowRight, Loader2, X } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DISMISS_KEY = (token) => `efgh_claim_dismiss_${token}`;

export function ClaimReportBanner({ token, onOpen, onClaimed }) {
  const [hint, setHint] = useState(null);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    if (typeof window !== "undefined" && localStorage.getItem(DISMISS_KEY(token)) === "1") {
      setDismissed(true);
    }
    axios.get(`${API}/public/reports/${token}/claim-hint`)
      .then((r) => setHint(r.data))
      .catch(() => setHint({ matched: false }));
  }, [token]);

  if (!hint || !hint.matched || dismissed) return null;

  if (hint.already_claimed) {
    return (
      <div
        className="rounded-2xl border border-efgh-green/25 bg-efgh-green/5 p-4 flex items-center gap-3"
        data-testid="claim-banner-already"
      >
        <CheckCircle2 className="w-5 h-5 text-efgh-green shrink-0" />
        <div className="flex-1 text-sm text-ink/85">
          Welcome back, <b>{hint.user_first_name}</b>. This report is already in your Health Timeline.
        </div>
        <a href="/patient" className="text-xs font-medium text-efgh-green underline">Open dashboard →</a>
      </div>
    );
  }

  return (
    <Card
      className="p-4 rounded-2xl border border-brand/30 bg-brand/5 flex items-start gap-3"
      data-testid="claim-banner"
    >
      <div className="w-9 h-9 rounded-lg bg-brand/15 text-brand grid place-items-center shrink-0">
        <ArrowRight className="w-4 h-4" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="font-heading font-semibold text-ink text-sm">
          Hi {hint.user_first_name} — this report matches your EFGH account.
        </div>
        <p className="text-xs text-ink/60 mt-0.5">
          Add it to your Health Timeline to view trends, biomarker history, and AI insights alongside your other reports.
        </p>
        <div className="mt-3 flex flex-wrap items-center gap-2">
          <Button size="sm" onClick={onOpen} className="bg-ink hover:bg-ink/90 text-white" data-testid="claim-open">
            Add to my Timeline
          </Button>
          <button
            onClick={() => { localStorage.setItem(DISMISS_KEY(token), "1"); setDismissed(true); if (onClaimed) onClaimed(false); }}
            className="text-xs text-ink/60 hover:text-ink underline"
            data-testid="claim-later"
          >
            Not now
          </button>
        </div>
      </div>
      <button
        onClick={() => { localStorage.setItem(DISMISS_KEY(token), "1"); setDismissed(true); }}
        className="w-6 h-6 rounded-lg text-ink/40 hover:bg-white/70 grid place-items-center"
        aria-label="Dismiss"
        data-testid="claim-dismiss"
      >
        <X className="w-3.5 h-3.5" />
      </button>
    </Card>
  );
}

export function ClaimReportModal({ token, open, onClose, onSuccess }) {
  const [step, setStep] = useState("otp"); // 'otp' (default: they already got the code via share) | 'channel' | 'done'
  const [channel, setChannel] = useState("whatsapp");
  const [channels, setChannels] = useState([]);
  const [hintMasked, setHintMasked] = useState("");
  const [otp, setOtp] = useState("");
  const [sending, setSending] = useState(false);
  const [verifying, setVerifying] = useState(false);

  useEffect(() => {
    if (!open) return;
    setStep("otp"); setOtp("");
    axios.get(`${API}/public/reports/${token}/claim-hint`)
      .then((r) => {
        setChannels(r.data?.channels || []);
        setChannel(r.data?.channels?.[0] || "whatsapp");
        setHintMasked(r.data?.hint_masked || "");
      })
      .catch(() => {});
  }, [open, token]);

  const send = async () => {
    setSending(true);
    try {
      const { data } = await axios.post(`${API}/public/reports/${token}/claim/send-otp`, { channel });
      setHintMasked(data.hint_masked || "");
      setStep("otp");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not send OTP. Try the other channel.");
    } finally {
      setSending(false);
    }
  };

  const verify = async () => {
    if (otp.length < 4) return toast.error("Enter the OTP you received");
    setVerifying(true);
    try {
      const { data } = await axios.post(`${API}/public/reports/${token}/claim/verify`, { otp });
      // Log the user in — persists token + user in localStorage to match /lib/auth.jsx
      localStorage.setItem("efgh_token", data.access_token);
      localStorage.setItem("efgh_user", JSON.stringify(data.user));
      setStep("done");
      onSuccess?.(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Incorrect or expired code");
    } finally {
      setVerifying(false);
    }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[9999] bg-black/40 grid place-items-center p-4" onClick={onClose} data-testid="claim-modal">
      <Card
        className="p-6 rounded-2xl w-full max-w-md relative"
        onClick={(e) => e.stopPropagation()}
      >
        <button onClick={onClose} className="absolute top-3 right-3 w-8 h-8 rounded-lg text-ink/50 hover:bg-ink/5 grid place-items-center" aria-label="Close" data-testid="claim-modal-close">
          <X className="w-4 h-4" />
        </button>

        {step === "channel" && (
          <div>
            <div className="font-heading font-bold text-xl text-ink">Resend your code</div>
            <p className="text-sm text-ink/60 mt-1">Where should we send it?</p>
            <div className="mt-4 grid gap-2">
              {channels.includes("whatsapp") && (
                <button
                  onClick={() => setChannel("whatsapp")}
                  className={`p-3 rounded-xl border text-left text-sm ${channel === "whatsapp" ? "border-brand bg-brand/5" : "border-border hover:border-ink/30"}`}
                  data-testid="claim-channel-whatsapp"
                >
                  <div className="font-medium text-ink">WhatsApp</div>
                  <div className="text-xs text-ink/50">To the number linked to your EFGH account</div>
                </button>
              )}
              {channels.includes("email") && (
                <button
                  onClick={() => setChannel("email")}
                  className={`p-3 rounded-xl border text-left text-sm ${channel === "email" ? "border-brand bg-brand/5" : "border-border hover:border-ink/30"}`}
                  data-testid="claim-channel-email"
                >
                  <div className="font-medium text-ink">Email</div>
                  <div className="text-xs text-ink/50">To the address linked to your EFGH account</div>
                </button>
              )}
            </div>
            <Button className="mt-5 w-full bg-ink hover:bg-ink/90 text-white" onClick={send} disabled={sending} data-testid="claim-send-otp">
              {sending ? <><Loader2 className="w-4 h-4 animate-spin mr-2" />Sending…</> : "Send code"}
            </Button>
            <button onClick={() => setStep("otp")} className="mt-3 text-xs text-ink/60 hover:text-ink underline block w-full text-center">
              ← I already have the code
            </button>
          </div>
        )}

        {step === "otp" && (
          <div>
            <div className="font-heading font-bold text-xl text-ink">Enter your access code</div>
            <p className="text-sm text-ink/60 mt-1">
              We messaged your code to <b>{hintMasked || "your WhatsApp / email"}</b> when this report was shared.
              Case-insensitive.
            </p>
            <Input
              autoFocus
              maxLength={16}
              className="mt-4 h-12 text-center text-lg tracking-[0.28em]"
              value={otp}
              onChange={(e) => setOtp(e.target.value.replace(/[^A-Za-z0-9]/g, ""))}
              placeholder="e.g. Name1234"
              data-testid="claim-otp-input"
            />
            <Button className="mt-4 w-full bg-ink hover:bg-ink/90 text-white" onClick={verify} disabled={verifying} data-testid="claim-verify">
              {verifying ? <><Loader2 className="w-4 h-4 animate-spin mr-2" />Verifying…</> : "Add to my Timeline"}
            </Button>
            {channels.length > 0 && (
              <button onClick={() => setStep("channel")} className="mt-3 text-xs text-ink/60 hover:text-ink underline block w-full text-center" data-testid="claim-resend">
                Didn't receive it? Resend code →
              </button>
            )}
          </div>
        )}

        {step === "done" && (
          <div className="text-center py-4">
            <CheckCircle2 className="w-14 h-14 mx-auto text-efgh-green" />
            <div className="mt-3 font-heading font-bold text-xl text-ink">Added to your Timeline</div>
            <p className="text-sm text-ink/60 mt-1">You're signed in. This report is now part of your Health Timeline alongside your other EFGH reports.</p>
            <a href="/patient" className="mt-5 inline-block">
              <Button className="bg-ink hover:bg-ink/90 text-white" data-testid="claim-go-dash">
                Open my dashboard <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </a>
          </div>
        )}
      </Card>
    </div>
  );
}
